import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { CallCategory } from "@expert/shared-types";
import type { LiteralUnion } from "type-fest";

import { getLogger } from "@expert/logging";
import { createImmerStore } from "@expert/state-utils";
import { freeze } from "immer";
import { useShallow } from "zustand/react/shallow";

import type { DeviceActivationCustomerState } from "../device-activation/types";
import type { GuideOption, GuideSelectionObject, UiMetadata } from "../logic-builder";
import type { OpenGuideSource } from "../types";

import { defaultUiMetadata } from "./defaults";

const logger = getLogger({
    module: "useGuideStore",
});

export interface SessionMetadata {
    [key: string]: unknown;
}

export interface AnalyticsInfo {
    currentStepId: string;
    completedSteps: string;
    completedStepCount: number;
}

export interface MonitoringProps {
    dispatcher: AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;
}

/**
 * Device Activation guide state for one Simplr room. `roomKey` is the raw workspace-side key
 * (`errorStateSessionId || sessionId`), not the suffixed Simplr ticket id `getRoomId` derives from it
 * via `toSimplrTicketId`: an error-state restart opens a new room that starts from the first step, so
 * state from the old room must not carry over.
 */
/**
 * Kept in memory only: `partialize` strips the whole slice from storage (it holds customer MDNs and
 * claim details), and `merge` keeps the in-memory copy when the store rehydrates.
 */
export interface DeviceActivationSlice {
    customerState: DeviceActivationCustomerState;
    /** Customer verification tokens. */
    extraAuthorizedToken?: string;
    roomKey: string;
    verifiedCustomerToken?: string;
    /**
     * Verizon only: the token `prepareVerizonReplacement` minted for the looked-up device, which activation
     * sends. Kept apart from `verifiedCustomerToken` (the PIN verification), which a new lookup still needs.
     */
    verizonReplacementToken?: string;
}

export type DeviceActivationTokens = Pick<
    DeviceActivationSlice,
    "extraAuthorizedToken" | "verifiedCustomerToken" | "verizonReplacementToken"
>;

export interface GuideSessionState {
    analyticsInfo: AnalyticsInfo;
    availableGuides: GuideSelectionObject[];
    currentGuideName?: string;
    deviceActivation?: DeviceActivationSlice;
    disableAutomaticOpen: boolean;
    errorStateSessionId?: string;
    isErrorState: boolean;
    isGuideOpen: boolean;
    isLoadingSimplrSession: boolean;
    isWaitingForNextStep: boolean;
    openGuideSource?: OpenGuideSource;
    recommendation?: LiteralUnion<CallCategory, string>;
    selectedOptions: Record<string, GuideOption>;
    sessionMetadata: SessionMetadata;
    uiMetadata: UiMetadata;
    supportedDevices?: string[];
    unsupportedStepId?: string;
    navigatedUnsupported: boolean;
}

const EMPTY_ANALYTICS_INFO: AnalyticsInfo = freeze({
    currentStepId: "",
    completedSteps: "",
    completedStepCount: 0,
});

const EMPTY_AVAILABLE_GUIDES: GuideSelectionObject[] = freeze([]);
const EMPTY_SELECTED_OPTIONS: Record<string, GuideOption> = freeze({});
const EMPTY_SESSION_METADATA: SessionMetadata = freeze({});

/** Frozen empty Device Activation customer state, returned for sessions/rooms with nothing stored. */
export const EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE: DeviceActivationCustomerState = freeze(
    {
        accountMdns: [],
        activationFlowType: undefined,
        claimSummaries: [],
        iccid: "",
        imei: "",
        phoneNumber: "",
        selectedClaimId: "",
        transactionId: "",
    },
    true,
);

/**
 * Frozen empty session sentinel. Returned by `selectGuideSession`/`useGuideSession` when a
 * sessionId has no entry yet — same reference every call so consumers don't re-render on
 * unrelated session updates. Frozen deeply so accidental mutation throws in dev.
 */
export const defaultGuideSessionState: GuideSessionState = freeze(
    {
        analyticsInfo: EMPTY_ANALYTICS_INFO,
        availableGuides: EMPTY_AVAILABLE_GUIDES,
        currentGuideName: undefined,
        deviceActivation: undefined,
        disableAutomaticOpen: false,
        errorStateSessionId: undefined,
        isErrorState: false,
        isGuideOpen: false,
        isLoadingSimplrSession: false,
        isWaitingForNextStep: false,
        openGuideSource: undefined,
        recommendation: undefined,
        selectedOptions: EMPTY_SELECTED_OPTIONS,
        sessionMetadata: EMPTY_SESSION_METADATA,
        uiMetadata: defaultUiMetadata,
        supportedDevices: undefined,
        unsupportedStepId: undefined,
        navigatedUnsupported: false,
    },
    true,
);

/** Mutable, per-session copy of the default state. Nested empties stay frozen until written. */
const newSessionState = (): GuideSessionState => ({
    analyticsInfo: { ...EMPTY_ANALYTICS_INFO },
    availableGuides: [],
    currentGuideName: undefined,
    deviceActivation: undefined,
    disableAutomaticOpen: false,
    errorStateSessionId: undefined,
    isErrorState: false,
    isGuideOpen: false,
    isLoadingSimplrSession: false,
    isWaitingForNextStep: false,
    openGuideSource: undefined,
    recommendation: undefined,
    selectedOptions: {},
    sessionMetadata: {},
    uiMetadata: defaultUiMetadata,
    supportedDevices: undefined,
    unsupportedStepId: undefined,
    navigatedUnsupported: false,
});

/** Standalone selector for non-hook contexts (e.g. `getState()` calls). Returns the frozen
 *  sentinel for missing sessions so reference equality holds across calls. */
export const selectGuideSession = (state: GuideStore, sessionId: string): GuideSessionState =>
    state.sessions[sessionId] ?? defaultGuideSessionState;

/** Subscribe to a session's full guide state. Uses shallow equality so individual property
 *  changes (not the session object identity) drive re-renders. Returns the frozen sentinel
 *  when the session has no entry. */
export const useGuideSession = (sessionId: string) =>
    useGuideStore(useShallow((state: GuideStore) => selectGuideSession(state, sessionId)));

/** The session's Device Activation slice, only if it belongs to the given room. */
const selectDeviceActivationSlice = (state: GuideStore, sessionId: string, roomKey: string) => {
    const slice = state.sessions[sessionId]?.deviceActivation;
    return slice?.roomKey === roomKey ? slice : undefined;
};

/** Device Activation customer state for a session's current room. Returns the frozen empty sentinel
 *  when nothing is stored, or when what is stored belongs to a previous room. */
export const selectDeviceActivationCustomerState = (
    state: GuideStore,
    sessionId: string,
    roomKey: string,
): DeviceActivationCustomerState =>
    selectDeviceActivationSlice(state, sessionId, roomKey)?.customerState ?? EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE;

export const selectVerifiedCustomerToken = (state: GuideStore, sessionId: string, roomKey: string) =>
    selectDeviceActivationSlice(state, sessionId, roomKey)?.verifiedCustomerToken ?? "";

export const selectExtraAuthorizedToken = (state: GuideStore, sessionId: string, roomKey: string) =>
    selectDeviceActivationSlice(state, sessionId, roomKey)?.extraAuthorizedToken ?? "";

export const selectVerizonReplacementToken = (state: GuideStore, sessionId: string, roomKey: string) =>
    selectDeviceActivationSlice(state, sessionId, roomKey)?.verizonReplacementToken ?? "";

/** The session's Device Activation slice for `roomKey`, replacing one left over from another room. */
const ensureDeviceActivationSlice = (session: GuideSessionState, roomKey: string) => {
    if (session.deviceActivation?.roomKey !== roomKey) {
        // Starts as the sentinel itself, so a slice holding only tokens still reads as "no customer state".
        session.deviceActivation = { customerState: EMPTY_DEVICE_ACTIVATION_CUSTOMER_STATE, roomKey };
    }
    return session.deviceActivation;
};

/**
 * Persisted shape: every session without its Device Activation slice.
 *
 * The slice is not persisted because it would not survive a reload anyway: this store rehydrates
 * manually (Workspace.tsx) from a parent effect, and child effects write to the store first (e.g.
 * useGuideManager's `setIsErrorState`), and every write overwrites storage. Writing customer PII to
 * localStorage would buy nothing until that ordering is fixed.
 */
const partializeGuideStore = (state: GuideStore) => ({
    sessions: Object.fromEntries(
        Object.entries(state.sessions).map(([id, session]) => {
            if (!session.deviceActivation) return [id, session];
            const persisted = { ...session };
            delete persisted.deviceActivation;
            return [id, persisted];
        }),
    ),
});

/** Default shallow merge, except each session keeps its in-memory Device Activation slice. */
const mergeGuideStore = (persisted: unknown, current: GuideStore): GuideStore => {
    if (!persisted || typeof persisted !== "object") return current;
    const merged = { ...current, ...(persisted as Partial<GuideStore>) };
    const sessions = { ...merged.sessions };
    for (const [id, session] of Object.entries(current.sessions)) {
        if (!session.deviceActivation) continue;
        sessions[id] = { ...(sessions[id] ?? session), deviceActivation: session.deviceActivation };
    }
    return { ...merged, sessions };
};

const ensureSession = (sessions: Record<string, GuideSessionState>, sessionId: string) => {
    if (!sessions[sessionId]) {
        sessions[sessionId] = newSessionState();
    }
    return sessions[sessionId];
};

export interface GuideStore {
    sessions: Record<string, GuideSessionState>;
    getGuideSession: (sessionId: string) => GuideSessionState;

    setAvailableGuides: (sessionId: string, availableGuides?: GuideSelectionObject[]) => void;
    setCurrentGuideName: (sessionId: string, props: MonitoringProps & { currentGuideName?: string }) => void;
    setAnalyticsInfo: (sessionId: string, analyticsInfo: AnalyticsInfo) => void;
    setDisableAutomaticOpen: (sessionId: string, value: boolean) => void;
    setErrorStateSessionId: (sessionId: string) => void;
    setIsErrorState: (sessionId: string, value: boolean) => void;
    setIsGuideOpen: (sessionId: string, value: boolean) => void;
    setIsLoadingSimplrSession: (sessionId: string, isLoading: boolean) => void;
    setIsWaitingForNextStep: (sessionId: string, isWaiting: boolean) => void;
    setOpenGuideSource: (sessionId: string, openGuideSource?: OpenGuideSource) => void;
    setSessionMetadata: (sessionId: string, sessionMetadata: SessionMetadata) => void;
    setUiMetadata: (sessionId: string, uiMetadata?: UiMetadata) => void;
    setSupportedDevices: (sessionId: string, supportedDevices?: string[]) => void;
    setUnsupportedStepId: (sessionId: string, unsupportedStepId?: string) => void;
    setNavigatedUnsupported: (sessionId: string, navigatedUnsupported: boolean) => void;
    setSelectedOptions: (sessionId: string, selectedOptions: Record<string, GuideOption>) => void;
    clearSelectedOptions: (sessionId: string) => void;
    setRecommendation: (sessionId: string, recommendation?: LiteralUnion<CallCategory, string>) => void;
    clearRecommendation: (sessionId: string) => void;
    setDeviceActivationCustomerState: (
        sessionId: string,
        roomKey: string,
        patch: Partial<DeviceActivationCustomerState>,
    ) => void;
    setDeviceActivationTokens: (sessionId: string, roomKey: string, tokens: DeviceActivationTokens) => void;
    clearDeviceActivationState: (sessionId: string) => void;
    resetGuide: (sessionId: string) => void;
    deleteSession: (sessionId: string) => void;
}

/**
 * Per-session guided-flow state for both voice and chat sessions.
 *
 * Persisted to localStorage with manual rehydration (see `useGuideStore.persist.rehydrate`), except
 * the Device Activation slice, which is kept in memory only (see `partializeGuideStore`).
 * Empty session reads return the frozen `defaultGuideSessionState` sentinel — never inline
 * `?? {}` here, or every consumer re-renders on unrelated session writes.
 *
 * For action calls, prefer `useGuideStore.getState().setX(...)` over a selector hook —
 * actions are stable references and don't need a subscription. Use the `useGuideSession`
 * hook for reading per-session state.
 */
export const useGuideStore = createImmerStore<GuideStore>({
    persist: { name: "guide-store", skipHydration: true, partialize: partializeGuideStore, merge: mergeGuideStore },
    devtools: {
        store: "simplr-guide-store",
        name: "simplr-guide-store",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    sessions: {},
    getGuideSession: (sessionId: string) => {
        return get().sessions[sessionId] ?? defaultGuideSessionState;
    },
    setAvailableGuides: (sessionId: string, availableGuides: GuideSelectionObject[] = []) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).availableGuides = availableGuides;
            },
            false,
            "setAvailableGuides",
        );
    },
    setCurrentGuideName: (
        sessionId: string,
        { dispatcher, currentGuideName }: MonitoringProps & { currentGuideName?: string },
    ) => {
        set(
            (state) => {
                const session = ensureSession(state.sessions, sessionId);
                if (session.currentGuideName && currentGuideName) {
                    // there is already a guide selected (a guide in progress)
                    logger.info(
                        { availableGuides: session.availableGuides, guide: currentGuideName },
                        "Solve Guide | open new guided flow within in progress guided flow",
                    );
                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                        guide: currentGuideName,
                        source: "manual",
                        sourceDetails: "in-progress-guide",
                    });
                } else if (currentGuideName) {
                    // a guided flow is being selected and started
                    logger.info(
                        { availableGuides: session.availableGuides, guide: currentGuideName },
                        "Solve Guide | open new guided flow",
                    );
                    void dispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                        guide: currentGuideName,
                        ...session.openGuideSource,
                    });
                }

                // Switching from one guide to another drops Device Activation answers. Transitions to or from
                // `undefined` don't: the name follows the message stream, which is briefly empty on chat switches
                // and reconnects, and clearing then would reintroduce the lost-MDN bug.
                if (session.currentGuideName && currentGuideName && session.currentGuideName !== currentGuideName) {
                    session.deviceActivation = undefined;
                }

                logger.info({ currentGuideName }, `Solve Guide | Setting currentGuideName`);
                session.currentGuideName = currentGuideName;
            },
            false,
            "setCurrentGuideName",
        );
    },
    setAnalyticsInfo: (sessionId: string, analyticsInfo: AnalyticsInfo) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).analyticsInfo = analyticsInfo;
            },
            false,
            "setAnalyticsInfo",
        );
    },
    setDisableAutomaticOpen: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).disableAutomaticOpen = value;
            },
            false,
            "setDisableAutomaticOpen",
        );
    },
    setErrorStateSessionId: (sessionId: string) => {
        set(
            (state) => {
                const session = ensureSession(state.sessions, sessionId);
                // Each call appends `_RESTARTED` so consecutive errors on the same workspace
                // session produce unique simplr session IDs (e.g. `${id}_RESTARTED_RESTARTED`).
                const base = session.errorStateSessionId ?? sessionId;
                session.errorStateSessionId = `${base}_RESTARTED`;
            },
            false,
            "setErrorStateSessionId",
        );
    },
    setIsErrorState: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isErrorState = value;
            },
            false,
            "setIsErrorState",
        );
    },
    setIsGuideOpen: (sessionId: string, value: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isGuideOpen = value;
            },
            false,
            "setIsGuideOpen",
        );
    },
    setIsLoadingSimplrSession: (sessionId: string, isLoading: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isLoadingSimplrSession = isLoading;
            },
            false,
            "setIsLoadingSimplrSession",
        );
    },
    setIsWaitingForNextStep: (sessionId: string, isWaiting: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).isWaitingForNextStep = isWaiting;
            },
            false,
            "setIsWaitingForNextStep",
        );
    },
    setOpenGuideSource: (sessionId: string, openGuideSource?: OpenGuideSource) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).openGuideSource = openGuideSource;
            },
            false,
            "setOpenGuideSource",
        );
    },
    setSessionMetadata: (sessionId: string, sessionMetadata: SessionMetadata) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).sessionMetadata = sessionMetadata;
            },
            false,
            "setSessionMetadata",
        );
    },
    setUiMetadata: (sessionId: string, uiMetadata?: UiMetadata) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).uiMetadata = uiMetadata || defaultUiMetadata;
            },
            false,
            "setUiMetadata",
        );
    },
    setSupportedDevices: (sessionId: string, supportedDevices?: string[]) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).supportedDevices = supportedDevices;
            },
            false,
            "setSupportedDevices",
        );
    },
    setUnsupportedStepId: (sessionId: string, unsupportedStepId?: string) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).unsupportedStepId = unsupportedStepId;
            },
            false,
            "setUnsupportedStepId",
        );
    },
    setNavigatedUnsupported: (sessionId: string, navigatedUnsupported: boolean) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).navigatedUnsupported = navigatedUnsupported;
            },
            false,
            "setNavigatedUnsupported",
        );
    },
    setSelectedOptions: (sessionId: string, selectedOptions: Record<string, GuideOption>) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).selectedOptions = selectedOptions;
            },
            false,
            "setSelectedOptions",
        );
    },
    clearSelectedOptions: (sessionId: string) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).selectedOptions = EMPTY_SELECTED_OPTIONS;
            },
            false,
            "clearSelectedOptions",
        );
    },
    setRecommendation: (sessionId: string, recommendation?: LiteralUnion<CallCategory, string>) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).recommendation = recommendation;
            },
            false,
            "setRecommendation",
        );
    },
    clearRecommendation: (sessionId: string) => {
        set(
            (state) => {
                ensureSession(state.sessions, sessionId).recommendation = undefined;
            },
            false,
            "clearRecommendation",
        );
    },
    setDeviceActivationCustomerState: (
        sessionId: string,
        roomKey: string,
        patch: Partial<DeviceActivationCustomerState>,
    ) => {
        set(
            (state) => {
                const slice = ensureDeviceActivationSlice(ensureSession(state.sessions, sessionId), roomKey);
                // Replace rather than mutate: a fresh slice holds the frozen sentinel, which is not a draft.
                slice.customerState = { ...slice.customerState, ...patch };
            },
            false,
            "setDeviceActivationCustomerState",
        );
    },
    setDeviceActivationTokens: (sessionId: string, roomKey: string, tokens: DeviceActivationTokens) => {
        set(
            (state) => {
                const slice = ensureDeviceActivationSlice(ensureSession(state.sessions, sessionId), roomKey);
                Object.assign(slice, tokens);
            },
            false,
            "setDeviceActivationTokens",
        );
    },
    clearDeviceActivationState: (sessionId: string) => {
        set(
            (state) => {
                const session = state.sessions[sessionId];
                if (session) session.deviceActivation = undefined;
            },
            false,
            "clearDeviceActivationState",
        );
    },
    resetGuide: (sessionId: string) => {
        set(
            (state) => {
                logger.info({ sessionId }, "Solve Guide | Resetting guide state");
                const session = ensureSession(state.sessions, sessionId);
                session.analyticsInfo = { ...EMPTY_ANALYTICS_INFO };
                // Clear so a new session can't auto-open against a prior session's
                // guides or recommendation before its Simplr stream has loaded.
                session.availableGuides = [];
                session.recommendation = undefined;
                session.currentGuideName = undefined;
                session.deviceActivation = undefined;
                session.disableAutomaticOpen = false;
                session.isErrorState = false;
                session.isLoadingSimplrSession = true;
                session.isWaitingForNextStep = false;
                session.errorStateSessionId = undefined;
                session.openGuideSource = undefined;
                session.selectedOptions = {};
                session.sessionMetadata = {};
                session.uiMetadata = defaultUiMetadata;
                session.supportedDevices = undefined;
                session.unsupportedStepId = undefined;
                session.navigatedUnsupported = false;
            },
            false,
            "resetGuide",
        );
    },
    deleteSession: (sessionId: string) => {
        set(
            (state) => {
                logger.info({ sessionId }, "Solve Guide | Deleting guide session");
                delete state.sessions[sessionId];
            },
            false,
            "deleteSession",
        );
    },
}));
